import { ART_PAL, ball, body, fade, mix, n, shade, star, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Garden scene behind the game zone (the SVG fallback until the raster scene is there): a vegetable garden at dusk
 * (a sunny morning in light) — a far line of soft hills, a sunflower and a leafy bush on the left, pumpkins and a
 * watering can on the right, carrot tops in the corners, fireflies at night. The centre stays calm (the reels sit
 * there): only the meadow and a soft pool of light. Nothing runs across the panels (no fences, no beams).
 * Motifs are marked `data-motif="<x>"` (tests). No filters, no text. Flowers are yellow, never white (docs/art-prompts.md).
 */
export function gardenScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `gd${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const horizon = wide ? 360 : 300;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const sky = P.dark ? mix(P.bg, P.blue, 0.22) : mix(P.sky, '#FFFFFF', 0.78);
  const skyLow = P.dark ? mix(P.bg, P.orange, 0.12) : mix(P.gold, '#FFFFFF', 0.8);
  const hill = P.dark ? mix(P.bg, P.green, 0.12) : mix(P.green, '#FFFFFF', 0.62);
  const meadow = P.dark ? mix(P.bg, P.green, 0.09) : mix(P.green, '#FFFFFF', 0.72);
  const soil = P.dark ? mix(P.bg, P.orange, 0.1) : mix(P.orange, '#FFFFFF', 0.7);
  const leaf = P.dark ? shade(P.green, 0.42) : mix(P.green, '#FFFFFF', 0.2);
  const petal = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.7);
  const pumpkin = P.dark ? shade(P.orange, 0.12) : tint(P.orange, 0.1);
  const can = P.dark ? shade(P.sky, 0.35) : tint(P.sky, 0.2);
  const moon = P.dark ? '#FFF1C2' : '#FFE7A3';

  const defs =
    `<defs>${vgrad(`${p}sk`, sky, skyLow, skyLow)}${vgrad(`${p}md`, meadow, meadow, soil)}` +
    fade(`${p}sun`, moon, P.dark ? 0.3 : 0.75) +
    ball(`${p}mo`, moon, 0.5, 0.12) +
    body(`${p}lf`, leaf, 0.3, 0.35, 0.3, 1) +
    body(`${p}pt`, petal, 0.35, 0.3) +
    ball(`${p}pk`, pumpkin, 0.35, 0.4) +
    body(`${p}cn`, can, 0.4, 0.35) +
    fade(`${p}ff`, '#FFE9A0', 0.8) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.4 : 0.5) +
    `</defs>`;

  /** a round leafy bush: three overlapping blobs */
  const bush = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" fill="url(#${p}lf)">` +
    `<circle cx="${n(x - 34 * s)}" cy="${n(y)}" r="${n(34 * s)}"/><circle cx="${n(x + 30 * s)}" cy="${n(y + 4 * s)}" r="${n(30 * s)}"/>` +
    `<circle cx="${n(x)}" cy="${n(y - 22 * s)}" r="${n(40 * s)}"/></g>`;
  /** a sunflower on a stalk: yellow petals, brown heart */
  const sunflower = (x: number, y: number, s: number, stalk: number) =>
    `<g data-motif="${Math.round(x)}"><path d="M${x} ${y}V${y + stalk}" stroke="${leaf}" stroke-width="${n(6 * s)}" stroke-linecap="round"/>` +
    `<path d="M${x} ${n(y + stalk * 0.55)}c${n(-30 * s)} ${n(-14 * s)} ${n(-44 * s)} ${n(4 * s)} ${n(-48 * s)} ${n(14 * s)}c${n(18 * s)} ${n(6 * s)} ${n(36 * s)} ${n(2 * s)} ${n(48 * s)} ${n(-14 * s)}Z" fill="url(#${p}lf)"/>` +
    `<polygon points="${star(x, y, 34 * s, 16 * s, 11)}" fill="url(#${p}pt)"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(14 * s)}" fill="${shade(P.orange, 0.55)}"/></g>`;
  /** a pumpkin: three ribs + a short stem */
  const pk = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" fill="url(#${p}pk)">` +
    `<ellipse cx="${n(x - 18 * s)}" cy="${y}" rx="${n(22 * s)}" ry="${n(26 * s)}"/><ellipse cx="${n(x + 18 * s)}" cy="${y}" rx="${n(22 * s)}" ry="${n(26 * s)}"/>` +
    `<ellipse cx="${x}" cy="${y}" rx="${n(22 * s)}" ry="${n(28 * s)}"/>` +
    `<path d="M${x} ${n(y - 26 * s)}l${n(4 * s)} ${n(-12 * s)}" stroke="${leaf}" stroke-width="${n(5 * s)}" stroke-linecap="round"/></g>`;
  /** carrot tops peeking out of the soil */
  const carrots = (x: number, y: number, s: number, k: number) => {
    let g = '';
    for (let i = 0; i < k; i++) {
      const cx = x + i * 46 * s;
      g +=
        `<path d="M${n(cx)} ${y}c${n(-10 * s)} ${n(-20 * s)} ${n(-14 * s)} ${n(-34 * s)} ${n(-8 * s)} ${n(-44 * s)}M${n(cx)} ${y}c${n(2 * s)} ${n(-22 * s)} ${n(6 * s)} ${n(-36 * s)} ${n(14 * s)} ${n(-42 * s)}"/>` +
        `<ellipse cx="${n(cx)}" cy="${n(y + 2 * s)}" rx="${n(7 * s)}" ry="${n(4 * s)}" fill="${pumpkin}" stroke="none"/>`;
    }
    return `<g data-motif="${Math.round(x)}" fill="none" stroke="${leaf}" stroke-width="${n(4 * s)}" stroke-linecap="round">${g}</g>`;
  };
  /** a watering can, spout to the left */
  const wcan = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    `<path d="M-30 -30H30L26 30H-26Z" fill="url(#${p}cn)"/>` +
    `<path d="M-28 -6L-70 -40" stroke="${can}" stroke-width="8" stroke-linecap="round"/>` +
    `<path d="M-14 -30C-14 -52 14 -52 14 -30" fill="none" stroke="${shade(can, 0.2)}" stroke-width="6"/></g>`;
  const fireflies = (pts: number[][]) =>
    P.dark
      ? pts
          .map(([x, y]) => `<circle cx="${x}" cy="${y}" r="10" fill="url(#${p}ff)"/><circle cx="${x}" cy="${y}" r="1.8" fill="#FFF3C4"/>`)
          .join('')
      : '';

  const moonX = wide ? 1300 : 320;
  const moonY = wide ? 140 : 90;
  const moonR = wide ? 30 : 20;
  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}sk)"/>` +
    (P.dark
      ? (wide ? [[240, 60], [620, 40], [1080, 70], [1460, 250], [360, 200]] : [[50, 160], [230, 40], [360, 200]])
          .map(([x, y]) => `<polygon points="${star(x!, y!, 2.2, 0.8, 4)}" fill="${moon}" fill-opacity=".55"/>`)
          .join('')
      : '') +
    `<circle cx="${moonX}" cy="${moonY}" r="${moonR * 4}" fill="url(#${p}sun)"/>` +
    `<circle data-motif="${moonX}" cx="${moonX}" cy="${moonY}" r="${moonR}" fill="url(#${p}mo)" fill-opacity="${o(0.85, 0.95)}"/>` +
    // far hills: one soft silhouette, low contrast
    `<path d="M0 ${horizon - 20}C${W * 0.15} ${horizon - 70} ${W * 0.3} ${horizon - 60} ${W * 0.45} ${horizon - 24}S${W * 0.72} ${horizon - 58} ${W * 0.86} ${horizon - 40}S${W} ${horizon - 30} ${W} ${horizon - 30}V${horizon + 10}H0Z" fill="${hill}" fill-opacity="${o(0.95, 0.6)}"/>` +
    `<rect y="${horizon}" width="${W}" height="${H - horizon}" fill="url(#${p}md)"/>` +
    `<ellipse cx="${W / 2}" cy="${H * 0.55}" rx="${n(W * 0.38)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      bush(250, 420, 1.1) +
      sunflower(310, 320, 1, 320) +
      sunflower(230, 480, 0.75, 200) +
      fireflies([[420, 250], [1250, 300], [1360, 480], [320, 640]]) +
      bush(1340, 420, 1) +
      pk(1300, 670, 1.1) +
      pk(1380, 710, 0.9) +
      pk(1250, 750, 0.8) +
      wcan(1350, 560, 0.75) +
      carrots(210, 790, 1.1, 3) +
      carrots(1260, 860, 1, 3);
  } else {
    art +=
      bush(385, 520, 0.7) +
      sunflower(34, 470, 0.7, 200) +
      fireflies([[70, 110], [300, 170], [120, 560]]) +
      pk(330, 575, 0.7) +
      carrots(20, 770, 0.8, 3);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Garden: a row of sprouts (repeat-x tile, 48 × 12). */
export function gardenAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const leaf = P.dark ? P.green : shade(P.green, 0.05);
  const soil = P.dark ? mix(P.orange, P.bg, 0.4) : mix(P.orange, '#FFFFFF', 0.35);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 12" width="48" height="12">` +
    `<path d="M0 10.5H48" stroke="${soil}" stroke-width="1.5" stroke-opacity=".7"/>` +
    `<path d="M24 10V6.5" stroke="${leaf}" stroke-width="1.3"/>` +
    `<path d="M24 7C21 7 19.5 5 19.5 3C22.5 3 24 5 24 7ZM24 6C27 6 28.5 4 28.5 2C25.5 2 24 4 24 6Z" fill="${leaf}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'garden', scene: gardenScene, accent: gardenAccent });
